// Share utilities for race cards
import { GAME_VERSIONS } from '../config/f1GameTracks';

const getGameVersionLabel = (gameVersion) =>
  gameVersion ? (GAME_VERSIONS.find(g => g.value === gameVersion)?.label || gameVersion) : 'F1 26';

// Helper function to get the appropriate race card image based on card type and fields
const getRaceCardImage = (card) => {
  // For career cards, use the existing logic
  if (card.cardType === 'career') {
    const rivalryType = card.rivalryType;
    const teamPreference = card.teamPreference;
    
    // Team name to abbreviation mapping
    const teamAbbreviations = {
      'Red Bull': 'rb',
      'Racing Bulls': 'visa',
      'Ferrari': 'ferrari',
      'McLaren': 'mclaren',
      'Mercedes': 'mercedes',
      'Aston Martin': 'aston',
      'Haas': 'haas',
      'Alpine': 'alpine',
      'Kick Sauber': 'sauber',
      'Williams': 'williams',
      'Any team': 'any'
    };
    
    // If a specific team is selected (not "Any team"), use team-specific image regardless of rivalry type
    if (teamPreference && teamPreference !== 'Any team') {
      const teamAbbr = teamAbbreviations[teamPreference] || 'any';
      return `${window.location.origin}/images/2-player-career/teammates-card-image-${teamAbbr}.jpg`;
    }
    
    // For "Any team" or no team preference, use rivalry-based logic
    switch (rivalryType) {
      case 'Teammates':
        // For "Any team + Teammates", show a specific teammates card image
        if (teamPreference === 'Any team') {
          return `${window.location.origin}/images/2-player-career/teammates-card-image-rb.jpg`;
        }
        // For other cases, use the team abbreviation
        const teamAbbr = teamAbbreviations[teamPreference] || 'any';
        return `${window.location.origin}/images/2-player-career/teammates-card-image-${teamAbbr}.jpg`;
      case 'Rival Teams':
      case 'Rival teams':
      case 'rival teams':
      case 'Any rivalry':
      case 'Any Rivalry':
      case 'any rivalry':
      default:
        // For "Any team" with "Any rivalry", deterministically select from 3 options based on card ID
        if (teamPreference === 'Any team') {
          const rivalImages = [
            `${window.location.origin}/images/2-player-career/rival-teams-option-5.jpg`,
            `${window.location.origin}/images/2-player-career/rival-teams-option-6.jpg`,
            `${window.location.origin}/images/2-player-career/rival-teams-option-7.jpg`
          ];
          // Use card ID to deterministically select an image
          const cardId = card.id || card.title || 'default';
          const hash = cardId.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
          }, 0);
          const imageIndex = Math.abs(hash) % rivalImages.length;
          return rivalImages[imageIndex];
        }
        // Fallback to option 6 for other cases
        return `${window.location.origin}/images/2-player-career/rival-teams-option-6.jpg`;
    }
  }
  
  // For league startup cards, use preview image
  if (card.cardType === 'leagueStartup') {
    return `${window.location.origin}/images/2-player-career/preview-image.png`;
  }
  
  // For unranked cards, use a generic unranked image
  if (card.cardType === 'unranked') {
    return `${window.location.origin}/images/unranked-lobby/unranked-lobby-image.jpg`;
  }
  
  // For reserve cards, use a generic reserve image
  if (card.cardType === 'reserve') {
    // Check for specific track images
    if (card.track === 'Australia') {
      return `${window.location.origin}/images/reserve-a-race/australia-image.svg`;
    }
    if (card.track === 'China') {
      return `${window.location.origin}/images/reserve-a-race/china-image.svg`;
    }
    if (card.track === 'Japan') {
      return `${window.location.origin}/images/reserve-a-race/japan-image.svg`;
    }
    if (card.track === 'Bahrain') {
      return `${window.location.origin}/images/reserve-a-race/bahrain-image.svg`;
    }
    if (card.track === 'Saudi Arabia') {
      return `${window.location.origin}/images/reserve-a-race/saudi-arabia-image.svg`;
    }
    if (card.track === 'Miami') {
      return `${window.location.origin}/images/reserve-a-race/miami-image.svg`;
    }
    if (card.track === 'Imola') {
      return `${window.location.origin}/images/reserve-a-race/imola-image.svg`;
    }
    if (card.track === 'Monaco') {
      return `${window.location.origin}/images/reserve-a-race/monaco-image.svg`;
    }
    if (card.track === 'Spain') {
      return `${window.location.origin}/images/reserve-a-race/spain-image.svg`;
    }
    if (card.track === 'Madrid') {
      return `${window.location.origin}/images/reserve-a-race/spain-image.svg`;
    }
    if (card.track === 'Canada') {
      return `${window.location.origin}/images/reserve-a-race/canada-image.svg`;
    }
    if (card.track === 'Austria') {
      return `${window.location.origin}/images/reserve-a-race/austria-image.svg`;
    }
    if (card.track === 'Britain') {
      return `${window.location.origin}/images/reserve-a-race/britain-image.svg`;
    }
    if (card.track === 'Belgium') {
      return `${window.location.origin}/images/reserve-a-race/belgium-image.svg`;
    }
    if (card.track === 'Hungary') {
      return `${window.location.origin}/images/reserve-a-race/hungary-image.svg`;
    }
    if (card.track === 'Netherlands') {
      return `${window.location.origin}/images/reserve-a-race/netherlands-image.svg`;
    }
    if (card.track === 'Monza') {
      return `${window.location.origin}/images/reserve-a-race/monza-image.svg`;
    }
    if (card.track === 'Azerbaijan') {
      return `${window.location.origin}/images/reserve-a-race/azerbaijan-image.svg`;
    }
    if (card.track === 'Singapore') {
      return `${window.location.origin}/images/reserve-a-race/singapore-image.svg`;
    }
    if (card.track === 'Austin') {
      return `${window.location.origin}/images/reserve-a-race/austin-image.svg`;
    }
    if (card.track === 'Mexico') {
      return `${window.location.origin}/images/reserve-a-race/mexico-image.svg`;
    }
    if (card.track === 'Brazil') {
      return `${window.location.origin}/images/reserve-a-race/brazil-image.svg`;
    }
    if (card.track === 'Las Vegas') {
      return `${window.location.origin}/images/reserve-a-race/las-vegas-image.svg`;
    }
    if (card.track === 'Qatar') {
      return `${window.location.origin}/images/reserve-a-race/qatar-image.svg`;
    }
    if (card.track === 'Abu Dhabi') {
      return `${window.location.origin}/images/reserve-a-race/abu-dhabi-image.svg`;
    }
    if (card.track === 'Austria (Reverse)') {
      return `${window.location.origin}/images/reserve-a-race/austria-reverse-image.svg`;
    }
    if (card.track === 'Britain (Reverse)') {
      return `${window.location.origin}/images/reserve-a-race/britain-reverse-image.svg`;
    }
    if (card.track === 'Netherlands (Reverse)') {
      return `${window.location.origin}/images/reserve-a-race/netherlands-reverse-image.svg`;
    }
    if (card.track === 'Portugal') {
      return `${window.location.origin}/images/reserve-a-race/portugal-image.svg`;
    }
    if (card.track === 'France') {
      return `${window.location.origin}/images/reserve-a-race/france-image.svg`;
    }
    if (card.track === 'Russia') {
      return `${window.location.origin}/images/reserve-a-race/russia-image.svg`;
    }
    // Default fallback for reserve cards
    return `${window.location.origin}/images/reserve-a-race/bahrain-image.svg`;
  }
  // For commentary cards, use commentary image
  if (card.cardType === 'commentary') {
    return `${window.location.origin}/images/commentary/commentary.png`;
  }
  
  // Default fallback
  return `${window.location.origin}/images/2-player-career/teammates-card-image-rb.jpg`;
};

export const shareRaceCard = (raceCard) => {
  const shareData = generateShareData(raceCard);
  
  // Check if Web Share API is available (mobile devices)
  if (navigator.share) {
    return navigator.share(shareData);
  }
  
  // Fallback to copying to clipboard
  return copyToClipboard(shareData.url);
};

export const generateShareData = (raceCard) => {
  const baseUrl = window.location.origin;
  const cardUrl = `${baseUrl}/race-cards`;
  
  // Get the appropriate image for this race card
  const cardImage = getRaceCardImage(raceCard);
  
  // Generate share text based on card type
  let shareText = '';
  
  const gameLabel = getGameVersionLabel(raceCard.gameVersion);
  if (raceCard.cardType === 'career') {
    shareText = `🏎️ Looking for a 2 player career partner in ${gameLabel}!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += `• AI Level: ${raceCard.aiLevel}\n`;
    shareText += `• Race Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Timezone: ${raceCard.timezone}\n`;
    shareText += `• Time: ${raceCard.timeOfDay}\n`;
    shareText += `• Frequency: ${raceCard.frequency}\n\n`;
  } else if (raceCard.cardType === 'leagueStartup') {
    shareText = `🏁 New league starting in ${gameLabel} – looking for drivers!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += raceCard.aiLevel === 'Multiple Tiers' ? `• Multiple Tiers\n` : `• AI Level: ${raceCard.aiLevel}\n`;
    shareText += `• Damage: ${raceCard.damage || 'N/A'}\n`;
    shareText += `• Assists: ${raceCard.assists || 'N/A'}\n`;
    shareText += `• Quali: ${raceCard.qualifyingFormat || 'N/A'}\n`;
    shareText += `• Race Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Timezone: ${raceCard.timezone}\n`;
    shareText += `• Time: ${raceCard.timeOfDay}\n`;
    shareText += `• Races: ${raceCard.races ?? 'N/A'}\n`;
    if (raceCard.leagueStartDate) {
      const d = raceCard.leagueStartDate?.seconds ? new Date(raceCard.leagueStartDate.seconds * 1000) : new Date(raceCard.leagueStartDate);
      shareText += `• Start Date: ${d.toLocaleDateString()}\n`;
    }
    if (raceCard.lobbyStreamed && raceCard.streamLink) {
      shareText += `• Stream: ${raceCard.streamLink}\n`;
    }
    shareText += `\n`;
  } else if (raceCard.cardType === 'unranked') {
    shareText = `🏁 Join my Unranked Lobby in ${gameLabel}!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += `• Car Type: ${raceCard.carType}\n`;
    shareText += `• Qualifying: ${raceCard.qualifyingPreference}\n`;
    shareText += `• Damage: ${raceCard.damage}\n`;
    shareText += `• Race Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Date: ${raceCard.raceDate ? new Date(raceCard.raceDate.seconds * 1000).toLocaleDateString() : 'TBD'}\n`;
    shareText += `• Start Time: ${raceCard.estimatedStartTime} GMT (${raceCard.timezone})\n\n`;
  } else if (raceCard.cardType === 'reserve') {
    shareText = `🎯 ${gameLabel} Reserve a Race spot available for ${raceCard.track}!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += `• AI Level: ${raceCard.estAiLevel}\n`;
    shareText += `• Race Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Date: ${raceCard.raceDate ? new Date(raceCard.raceDate.seconds * 1000).toLocaleDateString() : 'TBD'}\n`;
    shareText += `• Time: ${raceCard.startTime} GMT (${raceCard.timezone})\n\n`;
  } else if (raceCard.cardType === 'commentary') {
    shareText = `🎙️ Looking for a commentator for ${raceCard.track}!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += `• Race Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Date: ${raceCard.raceDate ? new Date(raceCard.raceDate.seconds * 1000).toLocaleDateString() : 'TBD'}\n`;
    shareText += `• Time: ${raceCard.startTime} GMT (${raceCard.timezone})\n\n`;
  } else {
    // Fallback for legacy cards
    shareText = `🏎️ F1 Game Race Card: ${raceCard.title}!\n\n`;
    shareText += `📊 Details:\n`;
    shareText += `• Team: ${raceCard.teamPreference}\n`;
    shareText += `• AI: ${raceCard.aiLevel}\n`;
    shareText += `• Rivalry: ${raceCard.rivalryType}\n`;
    shareText += `• Distance: ${raceCard.raceDistance}\n`;
    shareText += `• Timezone: ${raceCard.timezone}\n\n`;
  }
  
  shareText += `🔗 Check out more race cards on Race With Me!`;
  
  return {
    title: `${gameLabel} Race Card - ${raceCard.title}`,
    text: shareText,
    url: cardUrl,
    image: cardImage
  };
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return { success: true, message: 'Link copied to clipboard!' };
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return { success: true, message: 'Link copied to clipboard!' };
    } catch (fallbackErr) {
      document.body.removeChild(textArea);
      return { success: false, message: 'Failed to copy link' };
    }
  }
};

export const shareToX = (raceCard) => {
  const shareData = generateShareData(raceCard);
  const xUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(shareData.text)}&url=${encodeURIComponent(shareData.url)}`;
  window.open(xUrl, '_blank', 'width=600,height=400');
};



 